import { useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Switch } from '@/common/ui/switch'
import useMutationUpdateGameTypeProvider from '@/modules/game/api/hooks/useMutationUpdateGameTypeProvider'
import useQueryGetProviderGameTypeList from '@/modules/game/api/hooks/useQueryGetProviderGameTypeList'
import { useProviderGameTypeSelector } from '@/common/hooks/useProviderGameTypeSelector'
import { GameQueryKeys } from '@/modules/game/keys'
import showErrorToast from '@/common/utils/showErrorToast'

import {
	getPairedProvider,
	isProviderPaired,
} from '@/modules/game/constants/providerPairs'
import { ProviderPairWarningDialog } from '../ProviderPairWarningDialog'

import type { GameProviderWithBannerListTableData } from '@/modules/game/type'

interface IsActiveGameProviderProps {
	row: GameProviderWithBannerListTableData
}

export function IsActiveGameProvider({ row }: IsActiveGameProviderProps) {
	const queryClient = useQueryClient()
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [pendingValue, setPendingValue] = useState<boolean | null>(null)
	const [isLoading, setIsLoading] = useState(false)

	const { gameType } = useProviderGameTypeSelector()
	const { mutateAsync: updateGameProvider } =
		useMutationUpdateGameTypeProvider()

	// Background query ONLY for pair detection - fetches ALL providers
	const { data: allProvidersData } = useQueryGetProviderGameTypeList({
		skip: 0,
		take: 1000,
		gameType:
			(gameType as 'SLOT' | 'CASINO' | 'LOTTO' | 'SPORT' | 'FISHING') ||
			undefined,
	})

	const allProviders = useMemo(() => {
		return allProvidersData?.data || []
	}, [allProvidersData])

	const handleSwitchChange = async (value: boolean) => {
		console.log('🔍 === DEBUGGING START ===')
		console.log('Provider clicked:', row.name)
		console.log('Is paired?', isProviderPaired(row.name))

		const isPaired = isProviderPaired(row.name)

		if (!isPaired) {
			console.log('❌ Not paired, updating directly')
			await updateProvider(value)
			return
		}

		console.log('✅ Provider is paired!')

		const pairedProviderName = getPairedProvider(row.name)
		console.log('Paired provider should be:', pairedProviderName)

		if (!pairedProviderName) {
			console.log('❌ No paired provider name found')
			await updateProvider(value)
			return
		}

		console.log(
			'All providers in list:',
			allProviders.map((p) => p.name),
		)

		const pairedProvider = allProviders.find(
			(p) => p.name === pairedProviderName,
		)

		console.log('Found paired provider?', pairedProvider ? '✅ YES' : '❌ NO')

		if (pairedProvider) {
			console.log('Paired provider details:', {
				name: pairedProvider.name,
				isActive: pairedProvider.isActive,
				id: pairedProvider.id,
			})
		}

		if (!pairedProvider) {
			console.log(
				'❌ Paired provider not found in list, updating single provider',
			)
			console.log('Looking for:', pairedProviderName)
			console.log(
				'Available providers:',
				allProviders.map((p) => p.name).join(', '),
			)
			await updateProvider(value)
			return
		}

		// Always show dialog for paired providers to update both together
		console.log('✅ Showing dialog to update both providers')
		setPendingValue(value)
		setIsDialogOpen(true)
	}

	const updateProvider = async (value: boolean) => {
		setIsLoading(true)
		try {
			await updateGameProvider({
				id: row.id,
				payload: {
					isActive: value,
				},
			})
			queryClient.invalidateQueries({
				queryKey: [GameQueryKeys.GetGameProviderList],
			})
			toast.success('อัพเดทสถานะสำเร็จ')
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleConfirmPairedUpdate = async () => {
		if (pendingValue === null) return

		const pairedProviderName = getPairedProvider(row.name)
		if (!pairedProviderName) return

		const pairedProvider = allProviders.find(
			(p) => p.name === pairedProviderName,
		)
		if (!pairedProvider) return

		setIsLoading(true)
		try {
			await updateGameProvider({
				id: row.id,
				payload: {
					isActive: pendingValue,
				},
			})

			await updateGameProvider({
				id: pairedProvider.id,
				payload: {
					isActive: pendingValue,
				},
			})

			queryClient.invalidateQueries({
				queryKey: [GameQueryKeys.GetGameProviderList],
			})

			toast.success(
				`อัพเดทสถานะทั้ง ${row.name} และ ${pairedProviderName} สำเร็จ`,
			)
			setIsDialogOpen(false)
			setPendingValue(null)
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	const pairedProviderName = getPairedProvider(row.name)
	const pairedProvider = allProviders.find((p) => p.name === pairedProviderName)

	return (
		<>
			<Switch
				checked={row.isActive}
				onCheckedChange={handleSwitchChange}
				disabled={isLoading}
			/>

			{pairedProviderName && pairedProvider && (
				<ProviderPairWarningDialog
					open={isDialogOpen}
					onOpenChange={setIsDialogOpen}
					providerName={row.name}
					pairedProviderName={pairedProviderName}
					currentStatus={row.isActive}
					newStatus={pendingValue ?? row.isActive}
					pairedProviderStatus={pairedProvider.isActive}
					onConfirm={handleConfirmPairedUpdate}
					isLoading={isLoading}
				/>
			)}
		</>
	)
}
